import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { useDataQuery } from '@dhis2/app-runtime'
import { Card, LinearLoader } from '@dhis2/ui'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
         ResponsiveContainer, Cell, PieChart, Pie, Cell as PieCell, Legend } from 'recharts'
import { BarChart3, SearchIcon, PieChart as PieChartIcon,
         ViewIcon, CheckCircle, ChevronDown, ChevronRight } from 'lucide-react'
import { GiProgression } from 'react-icons/gi'
import { resolveActiveFiscalYear, getFallbackFiscalYear } from '../../GoalsManagement/utils/fiscalYearHelpers'
import { useFiscalYear } from '../../../shared/FiscalYearContext' 

const FISCAL_YEAR_CONFIG = {
  program: "NkReudUAW4F",
  stage: "htIWgNQWEMm",
  dataElements: {
    start: "U55MqwBcRAe",
    end: "kgdk4NXu4f5"
  }
};

const KPIKRAReports = ({ orgUnitId, performanceData }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [kpiSortBy, setKpiSortBy] = useState('progress');
  const [kpiSortOrder, setKpiSortOrder] = useState('desc');
  const [kpiSearchTerm, setKpiSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState({});

  const { fiscalYear, loading: fiscalYearLoading } = useFiscalYear();
    
  // ─── Helper: Check if event is within fiscal year ─────────────────
  const isEventInFiscalYear = useCallback((event) => {
    if (!fiscalYear?.start || !fiscalYear?.end) return true; // fallback: show all
    const dateValue = event?.eventDate || event?.occurredAt || event?.createdAt || event?.dueDate;
    if (!dateValue) return false;
    const itemDate = new Date(dateValue);
    const fiscalStart = new Date(fiscalYear.start);
    const fiscalEnd = new Date(fiscalYear.end);
    if (isNaN(itemDate.getTime()) || isNaN(fiscalStart.getTime()) || isNaN(fiscalEnd.getTime())) return false;
    fiscalEnd.setHours(23, 59, 59, 999);
    return itemDate >= fiscalStart && itemDate <= fiscalEnd;
  }, [fiscalYear]);

 
  // ─── Helper: Calculate objective progress from tasks ──────────────
  const calculateObjectiveProgress = useCallback((objectiveId, allTasks) => {
    if (!allTasks || allTasks.length === 0) return 0;
    const objectiveTasks = allTasks.filter(task => {
      const taskObjectiveId = task.dataValues?.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value || '';
      return taskObjectiveId === objectiveId;
    });
    if (objectiveTasks.length === 0) return 0;
    const totalTasks = objectiveTasks.length;
    const completedTasks = objectiveTasks.filter(task => {
      const status = task.dataValues?.find(dv => dv.dataElement === 'LuTwfuN34pc')?.value;
      return status === 'completed' || status === 'Completed';
    }).length;
    const totalProgress = objectiveTasks.reduce((sum, task) => {
      const progressValue = task.dataValues?.find(dv => dv.dataElement === 'AElpNxzTdRh')?.value;
      const progress = parseInt(progressValue) || 0;
      return sum + Math.min(Math.max(progress, 0), 100);
    }, 0);
    const averageProgress = totalProgress / totalTasks;
    const completionRate = (completedTasks / totalTasks) * 100;
    const weightedProgress = (completionRate * 0.7) + (averageProgress * 0.3);
    return Math.round(Math.min(weightedProgress, 100));
  }, []);


  // ─── Queries ────────────────────────────────────────────────────────
   // ─── Queries (only used if performanceData is not provided) ──────
  const { loading, error, data } = useDataQuery({
    objectives: {
      resource: 'events',
      params: {
        orgUnit: orgUnitId,
        program: 'yiT00XKrrHP',
        programStage: 'VIBtpvkpHFR',
        ouMode: 'ACCESSIBLE',
        pageSize: 1000,
        fields: 'event,orgUnit,orgUnitName,eventDate,dataValues[dataElement,value]'
      }
    },
    kpis: {
      resource: 'events',
      params: {
        orgUnit: orgUnitId,
        program: 'yiT00XKrrHP',
        programStage: 'ivyCLjNLFrd',
        ouMode: 'ACCESSIBLE',
        pageSize: 1000,
        fields: 'event,orgUnit,orgUnitName,eventDate,dataValues[dataElement,value]'
      }
    },
    tasks: {
      resource: 'events',
      params: {
        orgUnit: orgUnitId,
        program: 'yiT00XKrrHP',
        programStage: 'Nv9nYNcBTdM',
        ouMode: 'ACCESSIBLE',
        pageSize: 10000,
        fields: 'event,orgUnit,orgUnitName,eventDate,dataValues[dataElement,value]'
      }
    }
  }, {
    lazy: !orgUnitId,
    variables: { refreshKey }
  });


  // ─── Use performanceData if provided, else query data ────────────
  const rawData = performanceData || data;

 
  // ─── Filter KPIs by objective dates ────────────────────────────────
  const filteredData = useMemo(() => {
  if (!rawData) return { objectives: { events: [] }, kpis: { events: [] }, tasks: { events: [] } };

  const fyStart = fiscalYear?.start ? new Date(fiscalYear.start) : null;
  const fyEnd = fiscalYear?.end ? new Date(fiscalYear.end) : null;
  if (!fyStart || !fyEnd) {
    return {
      objectives: { events: rawData.objectives?.events || [] },
      kpis: { events: rawData.kpis?.events || [] },
      tasks: { events: rawData.tasks?.events || [] }
    };
  }
  fyEnd.setHours(23, 59, 59, 999);

  const objectives = rawData.objectives?.events || [];
  const objectiveDateMap = {};
  objectives.forEach(obj => {
    const dataValues = obj.dataValues || [];
    // Use objective's start/end dates; fallback to eventDate/dueDate
    let start = dataValues.find(dv => dv.dataElement === 'eDcVy661j9e')?.value || obj.eventDate;
    let end = dataValues.find(dv => dv.dataElement === 'J0rjxrDKn1l')?.value || obj.dueDate || obj.eventDate;
    if (start) start = new Date(start);
    if (end) end = new Date(end);
    objectiveDateMap[obj.event] = { start, end };
  });

  const isObjectiveInFY = (objId) => {
    const dates = objectiveDateMap[objId];
    if (!dates) return false;
    const { start, end } = dates;
    if (!start || !end) return false;
    // Overlap check: start <= fyEnd && end >= fyStart
    return start <= fyEnd && end >= fyStart;
  };

  // Filter objectives using the same logic
  const filteredObjectives = objectives.filter(obj => isObjectiveInFY(obj.event));

  // Filter KPIs based on parent objective's FY
  const filteredKPIs = (rawData.kpis?.events || []).filter(kpi => {
    const dataValues = kpi.dataValues || [];
    const parentObjectiveId = dataValues.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value;
    if (parentObjectiveId) {
      return isObjectiveInFY(parentObjectiveId);
    }
    return false; // exclude orphan KPIs
  });

  // Tasks: filter by eventDate (or could also use parent objective logic)
  const filterEventsByDate = (events) => (events || []).filter(isEventInFiscalYear);
  const filteredTasks = filterEventsByDate(rawData.tasks?.events);

  return {
    objectives: { events: filteredObjectives },
    kpis: { events: filteredKPIs },
    tasks: { events: filteredTasks }
  };
}, [rawData, isEventInFiscalYear, fiscalYear]);

  // ─── Extract KPIs ──────────────────────────────────────────────────
  const extractKPIs = useMemo(() => {
    const events = filteredData.kpis?.events || [];
    const allObjectives = filteredData.objectives?.events || [];
    const allTasks = filteredData.tasks?.events || [];
    
    const processed = events.map(kpi => {
      const dataValues = kpi.dataValues || [];
      const name = dataValues.find(dv => dv.dataElement === 'DBmylDQA9KX')?.value || 'Unnamed KPI';
      const description = dataValues.find(dv => dv.dataElement === 'rQqF75V9t7K')?.value || '';
      const targetValue = dataValues.find(dv => dv.dataElement === 'Ru68Ax2Xo2L')?.value;
      const target = targetValue ? parseInt(targetValue) : 100;
      
      const parentObjectiveId = dataValues.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value || '';
      const progress = parentObjectiveId ? calculateObjectiveProgress(parentObjectiveId, allTasks) : 0;
      
      const parentObjective = allObjectives.find(obj => 
        obj.event === parentObjectiveId || (obj.dataValues && obj.dataValues.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value === parentObjectiveId)
      );

      const achievement = Math.min(Math.max(progress, 0), 100);
      
      const linkedTasks = allTasks.filter(task => {
        const taskObjectiveId = task.dataValues?.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value || '';
        return taskObjectiveId === parentObjectiveId;
      });
      
      const completedTasks = linkedTasks.filter(task => {
        const status = task.dataValues?.find(dv => dv.dataElement === 'LuTwfuN34pc')?.value;
        return status === 'completed' || status === 'Completed';
      });

      const tasksList = linkedTasks.map(task => ({
        id: task.event,
        name: task.dataValues?.find(dv => dv.dataElement === 'I0QtZkMbzxN')?.value || 'Unnamed Task',
        progressPercent: parseInt(task.dataValues?.find(dv => dv.dataElement === 'AElpNxzTdRh')?.value) || 0,
        status: task.dataValues?.find(dv => dv.dataElement === 'LuTwfuN34pc')?.value || 'Not Started',
        completionDate: task.dataValues?.find(dv => dv.dataElement === 'J0rjxrDKn1l')?.value || ''
      }));

      return {
        id: kpi.event,
        name,
        description,
        target,
        progress: achievement,
        achievement,
        date: kpi.eventDate,
        parentObjectiveId,
        parentObjectiveName: parentObjective ? 
          parentObjective.dataValues?.find(dv => dv.dataElement === 'EuN5ztFnXsQ')?.value || 'Unknown Objective' : 'No Parent Objective',
        linkedTasks: linkedTasks.length,
        completedTasks: completedTasks.length,
        tasks: tasksList
      };
    });

    console.log('[KPIKRAReports] Extracted KPIs count:', processed.length);
    return processed;
  }, [filteredData.kpis, filteredData.objectives, filteredData.tasks, calculateObjectiveProgress]);

  
  // ─── Statistics ─────────────────────────────────────────────────────
  const statistics = useMemo(() => {
    const kpis = extractKPIs;
    const objectives = filteredData.objectives?.events || [];
    const tasks = filteredData.tasks?.events || [];
    
    const totalKPIs = kpis.length;
    const totalObjectives = objectives.length;
    const totalTasks = tasks.length;
    
    const avgKPIProgress = totalKPIs > 0 
      ? Math.round(kpis.reduce((sum, kpi) => sum + kpi.progress, 0) / totalKPIs)
      : 0;
      
    const completedKPIs = kpis.filter(kpi => kpi.progress >= 100).length;
    const completedTasks = tasks.filter(task => {
      const status = task.dataValues?.find(dv => dv.dataElement === 'LuTwfuN34pc')?.value;
      return status === 'completed' || status === 'Completed';
    }).length;

    const taskCompletionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    return {
      totalKPIs,
      totalObjectives,
      totalTasks,
      avgKPIProgress,
      completedKPIs,
      completedTasks,
      taskCompletionRate,
      kpiCompletionRate: totalKPIs > 0 ? Math.round((completedKPIs / totalKPIs) * 100) : 0,
    };
  }, [extractKPIs, filteredData.objectives, filteredData.tasks]);

  // ─── KPI Distribution for Pie Chart ──────────────────────────────
  const kpiDistributionData = useMemo(() => {
    const kpis = extractKPIs;
    const excellent = kpis.filter(k => k.progress >= 80).length;
    const good = kpis.filter(k => k.progress >= 60 && k.progress < 80).length;
    const needsWork = kpis.filter(k => k.progress >= 40 && k.progress < 60).length;
    const poor = kpis.filter(k => k.progress < 40).length;
    
    return [
      { name: 'Excellent (≥80%)', value: excellent, color: '#00C49F', progress: '80-100%' },
      { name: 'Good (60-79%)', value: good, color: '#FFBB28', progress: '60-79%' },
      { name: 'Needs Work (40-59%)', value: needsWork, color: '#FF8042', progress: '40-59%' },
      { name: 'Poor (<40%)', value: poor, color: '#FF4444', progress: '0-39%' }
    ].filter(item => item.value > 0);
  }, [extractKPIs]);

  // ─── Aggregated Data for Gauge ──────────────────────────────────
  const aggregatedData = useMemo(() => {
    return {
      kpi: {
        average: statistics.avgKPIProgress,
        completed: statistics.completedKPIs,
        total: statistics.totalKPIs,
        completionRate: statistics.kpiCompletionRate
      }
    };
  }, [statistics]);

  // ─── Sorting & Filtering ────────────────────────────────────────
  const sortedKPIs = useMemo(() => {
    const sorted = [...extractKPIs];
    sorted.sort((a, b) => {
      let aVal, bVal;
      switch(kpiSortBy) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'progress':
          aVal = a.progress;
          bVal = b.progress;
          break;
        case 'tasks':
          aVal = a.completedTasks / (a.linkedTasks || 1);
          bVal = b.completedTasks / (b.linkedTasks || 1);
          break;
        default:
          aVal = a.progress;
          bVal = b.progress;
      }
      if (kpiSortOrder === 'asc') return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });
    return sorted;
  }, [extractKPIs, kpiSortBy, kpiSortOrder]);

  const filteredKPIs = useMemo(() => {
    if (!kpiSearchTerm) return sortedKPIs;
    return sortedKPIs.filter(kpi => 
      kpi.name.toLowerCase().includes(kpiSearchTerm.toLowerCase()) ||
      kpi.parentObjectiveName.toLowerCase().includes(kpiSearchTerm.toLowerCase())
    );
  }, [sortedKPIs, kpiSearchTerm]);

  const toggleRow = (kpiId) => {
    setExpandedRows(prev => ({
      ...prev,
      [kpiId]: !prev[kpiId]
    }));
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return '#00C49F';
    if (progress >= 60) return '#FFBB28';
    if (progress >= 40) return '#FF8042';
    return '#FF4444';
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower.includes('excellent') || statusLower.includes('complete')) return '#00C49F';
    if (statusLower.includes('good')) return '#FFBB28';
    if (statusLower.includes('progress')) return '#0088FE';
    if (statusLower.includes('needs')) return '#FF8042';
    return '#6c757d';
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const useTableView = extractKPIs.length > 8;

  // ─── Loading States ────────────────────────────────────────────────
  if (!orgUnitId) {
    return (
      <Card style={{ padding: 16 }}>
        <h3>KPI Performance Dashboard</h3>
        <div style={{ textAlign: 'center', marginTop: 8, color: '#666' }}>
          Please select an organisation to view KPIs.
        </div>
      </Card>
    );
  }

  if ((loading || fiscalYearLoading) && !performanceData) {
    return (
      <Card style={{ padding: 16 }}>
        <h3>KPI Performance Dashboard</h3>
        <LinearLoader />
        <div style={{ textAlign: 'center', marginTop: 8, color: '#666' }}>
          {fiscalYearLoading ? 'Loading fiscal year...' : 'Loading KPI data...'}
        </div>
      </Card>
    );
  }

  if (error && !performanceData) {
    return (
      <Card style={{ padding: 16, marginBottom: 24 }}>
        <div style={{ textAlign: 'center', padding: 40, color: '#dc2626' }}>
          Error loading KPI data. Please try again.
        </div>
      </Card>
    );
  }

  return (
    <Card style={{ padding: 16, marginBottom: 24 }}>
      {/* Summary Statistics Cards */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 12,
        marginBottom: 24
      }}>
        <div style={{ 
          padding: 16,
          backgroundColor: '#e7f3ff',
          borderRadius: 8,
          textAlign: 'center',
          border: '1px solid #b3d9ff'
        }}>
          <div style={{ fontWeight: '600', color: '#0088FE', fontSize: 12 }}>TOTAL KPIs</div>
          <div style={{ fontSize: 24, fontWeight: 'bold', color: '#0056b3' }}>{statistics.totalKPIs}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{statistics.completedKPIs} completed</div>
        </div>
        
        <div style={{ 
          padding: 16,
          backgroundColor: '#e6f7f0',
          borderRadius: 8,
          textAlign: 'center',
          border: '1px solid #a3e9cb'
        }}>
          <div style={{ fontWeight: '600', color: '#00C49F', fontSize: 12 }}>AVG KPI PROGRESS</div>
          <div style={{ fontSize: 24, fontWeight: 'bold', color: '#00875a' }}>{statistics.avgKPIProgress}%</div>
          <div style={{ fontSize: 12, color: '#666' }}>{statistics.kpiCompletionRate}% completion rate</div>
        </div>
        
        <div style={{ 
          padding: 16,
          backgroundColor: '#ffe6e6',
          borderRadius: 8,
          textAlign: 'center',
          border: '1px solid #ffb3b3'
        }}>
          <div style={{ fontWeight: '600', color: '#FF8042', fontSize: 12 }}>TASK COMPLETION</div>
          <div style={{ fontSize: 24, fontWeight: 'bold', color: '#cc0000' }}>{statistics.taskCompletionRate}%</div>
          <div style={{ fontSize: 12, color: '#666' }}>{statistics.completedTasks}/{statistics.totalTasks} tasks</div>
        </div>
      </div>

      {/* KPI Performance Summary Chart */}
      {extractKPIs.length > 0 && (
        <div style={{ 
          marginBottom: 24,
          padding: 16,
          backgroundColor: '#f8f9fa',
          borderRadius: 8,
          border: '1px solid #dee2e6'
        }}>
          <h4 style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <PieChartIcon size={18} />
            KPI Performance Summary
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {/* KPI Donut Chart */}
            <div>
              <div style={{ height: 250 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={kpiDistributionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {kpiDistributionData.map((entry, index) => (
                        <PieCell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* KPI Gauge Summary */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'center',
              alignItems: 'center',
              gap: 16
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 14, color: '#666', marginBottom: 8 }}>Overall Average Progress</div>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <div style={{
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    background: `conic-gradient(
                      ${aggregatedData.kpi.average >= 80 ? '#00C49F' : aggregatedData.kpi.average >= 60 ? '#FFBB28' : '#FF8042'} 0deg ${aggregatedData.kpi.average * 3.6}deg,
                      #e9ecef ${aggregatedData.kpi.average * 3.6}deg 360deg
                    )`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <div style={{
                      width: 90,
                      height: 90,
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column'
                    }}>
                      <div style={{ fontSize: 28, fontWeight: 'bold', color: aggregatedData.kpi.average >= 80 ? '#00C49F' : aggregatedData.kpi.average >= 60 ? '#FFBB28' : '#FF8042' }}>
                        {aggregatedData.kpi.average}%
                      </div>
                      <div style={{ fontSize: 10, color: '#666' }}>Average</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: 24, marginTop: 8 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 'bold', color: '#00C49F' }}>
                    {aggregatedData.kpi.completionRate}%
                  </div>
                  <div style={{ fontSize: 11, color: '#666' }}>Completion Rate</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 'bold', color: '#0088FE' }}>
                    {aggregatedData.kpi.completed}/{aggregatedData.kpi.total}
                  </div>
                  <div style={{ fontSize: 11, color: '#666' }}>Completed KPIs</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* KPI Section */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h4 style={{ margin: 0 }}>Key Performance Indicators ({statistics.totalKPIs})</h4>
          <button
            onClick={handleRefresh}
            style={{
              padding: '6px 12px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 12
            }}
          >
            Refresh
          </button>
        </div>
        
        {extractKPIs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#6c757d', backgroundColor: '#f8f9fa', borderRadius: 8 }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}><BarChart3 /></div>
            <div style={{ fontWeight: '600', marginBottom: 4 }}>No KPIs Found</div>
            <div>Create KPIs to track performance metrics</div>
          </div>
        ) : useTableView ? (
          // Table View for many KPIs
          <div>
            {/* Search and Filter Bar */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: 16,
              gap: 12,
              flexWrap: 'wrap'
            }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <input
                  type="text"
                  placeholder="Search KPIs or objectives..."
                  value={kpiSearchTerm}
                  onChange={(e) => setKpiSearchTerm(e.target.value)}
                  style={{
                    width: '50%',
                    padding: '8px 12px',
                    border: '1px solid #dee2e6',
                    borderRadius: 4,
                    fontSize: 13,
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <select
                  value={kpiSortBy}
                  onChange={(e) => setKpiSortBy(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #dee2e6',
                    borderRadius: 4,
                    fontSize: 13,
                    backgroundColor: 'white'
                  }}
                >
                  <option value="progress">Sort by Progress</option>
                  <option value="name">Sort by Name</option>
                  <option value="tasks">Sort by Task Completion</option>
                </select>
                <button
                  onClick={() => setKpiSortOrder(kpiSortOrder === 'asc' ? 'desc' : 'asc')}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #dee2e6',
                    borderRadius: 4,
                    fontSize: 13,
                    cursor: 'pointer',
                    backgroundColor: '#2c4af5',
                    color: 'white'
                  }}
                >
                  {kpiSortOrder === 'asc' ? '↑ Ascending' : '↓ Descending'}
                </button>
              </div>
            </div>

            {/* Summary Stats */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '12px 16px',
              backgroundColor: '#e7f3ff',
              borderRadius: 6,
              fontSize: 12,
              marginBottom: 16
            }}>
              <span>Showing {filteredKPIs.length} of {extractKPIs.length} KPIs</span>
              <span>Average Progress: {statistics.avgKPIProgress}%</span>
              <span>Completed: {statistics.completedKPIs}</span>
            </div>

            {/* Table View */}
            <div style={{ 
              maxHeight: 500, 
              overflowY: 'auto',
              border: '1px solid #dee2e6',
              borderRadius: 6
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                  <tr>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6', width: 30 }}></th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>KPI Name</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Objective</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>Progress</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>Tasks</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredKPIs.map((kpi) => {
                    const isExpanded = expandedRows[kpi.id];
                    const hasTasks = kpi.tasks && kpi.tasks.length > 0;
                    
                    return (
                      <React.Fragment key={kpi.id}>
                        <tr 
                          style={{ 
                            borderBottom: isExpanded ? 'none' : '1px solid #f0f0f0',
                            backgroundColor: isExpanded ? '#f8f9fa' : 'transparent',
                            cursor: hasTasks ? 'pointer' : 'default'
                          }}
                          onClick={() => hasTasks && toggleRow(kpi.id)}
                        >
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            {hasTasks && (
                              <span>
                                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '12px', fontWeight: '500' }}>{kpi.name}</td>
                          <td style={{ padding: '12px', fontSize: 12, color: '#6c757d' }}>{kpi.parentObjectiveName}</td>
                          <td style={{ padding: '12px', width: 200 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ flex: 1 }}>
                                <div style={{
                                  width: '100%',
                                  height: 6,
                                  backgroundColor: '#e9ecef',
                                  borderRadius: 3,
                                  overflow: 'hidden'
                                }}>
                                  <div style={{
                                    width: `${kpi.progress}%`,
                                    height: '100%',
                                    backgroundColor: getProgressColor(kpi.progress),
                                    borderRadius: 3
                                  }} />
                                </div>
                              </div>
                              <span style={{ fontSize: 12, fontWeight: '600', minWidth: 40 }}>
                                {kpi.progress}%
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', fontSize: 12 }}>
                            {kpi.completedTasks}/{kpi.linkedTasks}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: 12,
                              fontSize: 11,
                              fontWeight: '600',
                              backgroundColor: kpi.progress >= 80 ? '#d4edda' : kpi.progress >= 60 ? '#fff3cd' : '#f8d7da',
                              color: kpi.progress >= 80 ? '#155724' : kpi.progress >= 60 ? '#856404' : '#721c24'
                            }}>
                              {kpi.progress >= 80 ? 'Excellent' : kpi.progress >= 60 ? 'Good' : 'Needs Work'}
                            </span>
                          </td>
                        </tr>
                        
                        {/* Expanded Row - Shows Tasks */}
                        {isExpanded && hasTasks && (
                          <tr style={{ backgroundColor: '#f9f9f9' }}>
                            <td colSpan="6" style={{ padding: '0' }}>
                              <div style={{ 
                                padding: '16px 20px 16px 48px',
                                borderTop: '1px solid #e0e0e0',
                                backgroundColor: '#fafafa'
                              }}>
                                <div style={{ marginBottom: '12px', fontWeight: '600', fontSize: '0.85rem', color: '#666' }}>
                                  Related Tasks ({kpi.tasks.length})
                                </div>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                                  <thead>
                                    <tr style={{ borderBottom: '1px solid #ddd' }}>
                                      <th style={{ textAlign: 'left', padding: '8px' }}>Task Name</th>
                                      <th style={{ textAlign: 'left', padding: '8px' }}>Progress</th>
                                      <th style={{ textAlign: 'left', padding: '8px' }}>Status</th>
                                      <th style={{ textAlign: 'left', padding: '8px' }}>Completion Date</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {kpi.tasks.map((task, taskIdx) => (
                                      <tr key={task.id || taskIdx} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={{ padding: '8px' }}>{task.name}</td>
                                        <td style={{ padding: '8px' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div style={{ width: '80px', backgroundColor: '#e0e0e0', borderRadius: '10px', overflow: 'hidden' }}>
                                              <div style={{
                                                width: `${task.progressPercent}%`,
                                                height: '6px',
                                                backgroundColor: getProgressColor(task.progressPercent),
                                                borderRadius: '10px'
                                              }} />
                                            </div>
                                            <span>{task.progressPercent}%</span>
                                          </div>
                                        </td>
                                        <td style={{ padding: '8px' }}>
                                          <span style={{
                                            padding: '2px 6px',
                                            borderRadius: '10px',
                                            fontSize: '0.7rem',
                                            backgroundColor: getStatusColor(task.status),
                                            color: 'white'
                                          }}>
                                            {task.status || 'Not Started'}
                                          </span>
                                        </td>
                                        <td style={{ padding: '8px' }}>{task.completionDate || '—'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                            
                         </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              
              {filteredKPIs.length === 0 && (
                <div style={{ textAlign: 'center', padding: 40, color: '#6c757d' }}>
                  No KPIs match your search
                </div>
              )}
            </div>
          </div>
        ) : (
          // Card View for fewer KPIs
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {extractKPIs.map((kpi) => (
              <div key={kpi.id} style={{ 
                padding: 12, 
                marginBottom: 8, 
                border: '1px solid #e9ecef',
                borderRadius: 6,
                backgroundColor: '#ffffff',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontWeight: '600', fontSize: 13 }}>
                    {kpi.name}
                  </div>
                  <div style={{ 
                    padding: '2px 8px',
                    backgroundColor: kpi.progress >= 80 ? '#00C49F' : kpi.progress >= 60 ? '#FFBB28' : '#FF8042',
                    color: 'white',
                    borderRadius: 12,
                    fontSize: 11,
                    fontWeight: 'bold'
                  }}>
                    {kpi.progress}%
                  </div>
                </div>
                
                <div style={{ marginBottom: 6 }}>
                  <div style={{ 
                    width: '100%',
                    height: 6,
                    backgroundColor: '#e9ecef',
                    borderRadius: 3,
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${kpi.progress}%`,
                      height: '100%',
                      backgroundColor: kpi.progress >= 80 ? '#00C49F' : kpi.progress >= 60 ? '#FFBB28' : '#FF8042',
                      borderRadius: 3,
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
                
                <div style={{ fontSize: 11, color: '#6c757d', display: 'flex', justifyContent: 'space-between' }}>
                  <span>🎯 {kpi.parentObjectiveName}</span>
                  <span>✅ {kpi.completedTasks}/{kpi.linkedTasks} tasks</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  )
}

export default KPIKRAReports